import React, { useState, useEffect } from 'react';
import {
  Building2,
  Phone,
  Landmark,
  Calendar,
  FileText,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Loader2,
} from 'lucide-react';
import { fetchMerchants, activateMerchant, resendPasswordResetLink } from '../services/merchantService';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../shared/providers/LanguageProvider';
import { ShimmerTable, ShimmerBox } from '../components/common/Shimmer';
import { PageHeader } from '@/components/ui/page-header';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

const API_URL = process.env.REACT_APP_API_URL;

const InfoItem = ({ label, value, className = '' }) => (
  <div className={className}>
    <div className="mb-1 text-xs font-medium text-muted-foreground">{label}</div>
    <div className="break-words text-sm font-semibold text-foreground">{value || '-'}</div>
  </div>
);

const Merchants = () => {
  const { token } = useAuth();
  const { t } = useLanguage();
  const [merchants, setMerchants] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(null);

  const [activationModal, setActivationModal] = useState({
    show: false,
    type: null,
    message: '',
    data: null,
    merchantId: null,
  });

  useEffect(() => {
    setLoading(true);
    setError(null);

    fetchMerchants(token)
      .then((data) => {
        if (Array.isArray(data)) {
          setMerchants(data);
        } else {
          console.warn('fetchMerchants returned non-array data:', data);
          setMerchants([]);
          setError('Invalid data format received from server');
        }
      })
      .catch((err) => {
        console.error('Error fetching merchants:', err);
        setError(err.message || 'Failed to load merchants');
        setMerchants([]);
      })
      .finally(() => setLoading(false));
  }, [token]);

  const handleSuspend = async (id) => {
    setActionLoading(true);
    setActionError(null);
    try {
      const response = await fetch(`${API_URL}/api/admin/merchants/${id}/suspend`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error('Failed to suspend merchant');
      setMerchants((prev) => prev.map((m) => (m.id === id ? { ...m, status: 'suspended' } : m)));
    } catch (err) {
      setActionError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleActivate = async (id) => {
    setActivationModal({
      show: true,
      type: 'loading',
      message: t('activating'),
      data: null,
      merchantId: id,
    });

    setActionLoading(true);
    setActionError(null);

    try {
      const response = await activateMerchant(id, token, { send_welcome_email: true });

      setMerchants((prev) => prev.map((m) => (m.id === id ? { ...m, status: 'active' } : m)));

      if (response.status === 200) {
        if (response.data?.password_setup?.email_sent === false && response.data?.warning) {
          setActivationModal({
            show: true,
            type: 'partial',
            message: response.message || t('accountActivatedButEmailFailed'),
            data: response.data,
            merchantId: id,
          });
        } else {
          setActivationModal({
            show: true,
            type: 'success',
            message: response.message || t('merchantActivatedSuccessfully'),
            data: response.data,
            merchantId: id,
          });
        }
      } else if (response.status === 207) {
        setActivationModal({
          show: true,
          type: 'partial',
          message: response.message || t('accountActivatedButEmailFailed'),
          data: response.data,
          merchantId: id,
        });
      } else if (response.status === 409) {
        setActivationModal({
          show: true,
          type: 'error',
          message: response.error || t('userAlreadyExists'),
          data: response.details,
          merchantId: id,
        });
        setMerchants((prev) => prev.map((m) => (m.id === id ? { ...m, status: 'suspended' } : m)));
      } else if (!response.success) {
        setActivationModal({
          show: true,
          type: 'error',
          message: response.error || t('failedToActivateMerchant'),
          data: response.details,
          merchantId: id,
        });
        setMerchants((prev) => prev.map((m) => (m.id === id ? { ...m, status: 'suspended' } : m)));
      }
    } catch (err) {
      console.error('Error activating merchant:', err);
      setActionError(err.message);
      setActivationModal({
        show: true,
        type: 'error',
        message: err.message || t('failedToActivateMerchant'),
        data: null,
        merchantId: id,
      });
      setMerchants((prev) => prev.map((m) => (m.id === id ? { ...m, status: 'suspended' } : m)));
    } finally {
      setActionLoading(false);
    }
  };

  const handleResendEmail = async (merchantId) => {
    setActivationModal((prev) => ({ ...prev, type: 'loading', message: t('resendingEmail') }));

    try {
      const response = await resendPasswordResetLink(merchantId, token);

      if (response.success) {
        setActivationModal({
          show: true,
          type: 'success',
          message: response.message || t('passwordResetLinkSentSuccessfully'),
          data: response.data,
          merchantId: merchantId,
        });
      } else {
        setActivationModal({
          show: true,
          type: 'error',
          message: response.error || t('emailFailedToSend'),
          data: response.details,
          merchantId: merchantId,
        });
      }
    } catch (err) {
      console.error('Error resending email:', err);
      setActivationModal({
        show: true,
        type: 'error',
        message: err.message || t('emailFailedToSend'),
        data: null,
        merchantId: merchantId,
      });
    }
  };

  const closeActivationModal = () => {
    setActivationModal({
      show: false,
      type: null,
      message: '',
      data: null,
      merchantId: null,
    });
  };

  const filteredMerchants = Array.isArray(merchants)
    ? merchants.filter(
        (m) =>
          m.email?.toLowerCase().includes(search.toLowerCase()) ||
          m.business_name?.toLowerCase().includes(search.toLowerCase())
      )
    : [];

  return (
    <div>
      <PageHeader title={t('merchants')} />
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <Input
          type="text"
          placeholder={t('search') + ' by name or email...'}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full sm:w-64"
        />
      </div>
      {loading ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <ShimmerBox width="w-64" height="h-10" />
            <ShimmerBox width="w-32" height="h-10" />
          </div>
          <ShimmerTable rows={8} columns={4} />
        </div>
      ) : error ? (
        <div className="py-8 text-center text-destructive">{error}</div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('businessName')}</TableHead>
                  <TableHead>{t('email')}</TableHead>
                  <TableHead>{t('status')}</TableHead>
                  <TableHead>{t('actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMerchants.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="py-6 text-center text-muted-foreground">
                      {search ? t('noResultsFound') : t('noMerchantsFound')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredMerchants.map((m) => (
                    <TableRow key={m.id}>
                      <TableCell>
                        <button
                          type="button"
                          className="cursor-pointer text-start hover:underline"
                          onClick={() => setSelectedMerchant(m)}
                        >
                          {m.business_name || '-'}
                        </button>
                      </TableCell>
                      <TableCell>{m.email}</TableCell>
                      <TableCell>
                        <Badge variant={m.status === 'active' ? 'success' : 'destructive'}>
                          {m.status === 'active' ? t('active') : t('suspended')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-2">
                          <Button size="sm" variant="secondary" onClick={() => setSelectedMerchant(m)}>
                            {t('view')}
                          </Button>
                          {m.status === 'active' ? (
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleSuspend(m.id)}
                              disabled={actionLoading}
                            >
                              {t('suspend')}
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => handleActivate(m.id)}
                              disabled={actionLoading}
                            >
                              {t('activate')}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Dialog
        open={!!selectedMerchant}
        onOpenChange={(open) => {
          if (!open) setSelectedMerchant(null);
        }}
      >
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
          {selectedMerchant && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  {selectedMerchant.business_name || t('merchantDetails')}
                </DialogTitle>
                <DialogDescription>{selectedMerchant.email}</DialogDescription>
              </DialogHeader>

              <div className="space-y-6">
                <div className="flex items-center justify-between rounded-lg bg-muted/50 p-4">
                  <span className="text-sm font-medium text-muted-foreground">{t('status')}</span>
                  <Badge variant={selectedMerchant.status === 'active' ? 'success' : 'destructive'}>
                    {selectedMerchant.status === 'active' ? t('active') : t('suspended')}
                  </Badge>
                </div>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Building2 className="h-4 w-4" />
                      {t('businessInformation')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <InfoItem label={t('businessName')} value={selectedMerchant.business_name} />
                    <InfoItem label={t('legalName')} value={selectedMerchant.legal_name} />
                    <InfoItem label={t('crNumber')} value={selectedMerchant.cr_number} />
                    <InfoItem label={t('businessCategory')} value={selectedMerchant.business_category} />
                    <InfoItem label={t('city')} value={selectedMerchant.city} />
                    <InfoItem
                      label={t('address')}
                      value={selectedMerchant.address}
                      className="md:col-span-2"
                    />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Phone className="h-4 w-4" />
                      {t('contactInformation')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <InfoItem label={t('email')} value={selectedMerchant.email} />
                    <InfoItem label={t('phone')} value={selectedMerchant.phone} />
                    <InfoItem label={t('username')} value={selectedMerchant.username} />
                    <InfoItem
                      label="User ID"
                      value={
                        selectedMerchant.user_id
                          ? selectedMerchant.user_id.substring(0, 20) + '...'
                          : '-'
                      }
                    />
                  </CardContent>
                </Card>

                {(selectedMerchant.bank_account_iban || selectedMerchant.bank_account_holder) && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Landmark className="h-4 w-4" />
                        {t('bankInformation')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <InfoItem
                        label={t('bankAccountIBAN')}
                        value={selectedMerchant.bank_account_iban}
                      />
                      <InfoItem
                        label={t('bankAccountHolder')}
                        value={selectedMerchant.bank_account_holder}
                      />
                    </CardContent>
                  </Card>
                )}

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Calendar className="h-4 w-4" />
                      {t('timeline')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <InfoItem
                      label={t('createdAt')}
                      value={
                        selectedMerchant.created_at
                          ? new Date(selectedMerchant.created_at).toLocaleString('ar-SA')
                          : '-'
                      }
                    />
                    <InfoItem
                      label={t('updatedAt')}
                      value={
                        selectedMerchant.updated_at
                          ? new Date(selectedMerchant.updated_at).toLocaleString('ar-SA')
                          : '-'
                      }
                    />
                  </CardContent>
                </Card>

                {selectedMerchant.description && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center gap-2 text-base">
                        <FileText className="h-4 w-4" />
                        {t('description')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="whitespace-pre-wrap text-muted-foreground">
                        {selectedMerchant.description}
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>

              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setSelectedMerchant(null)}>
                  {t('close')}
                </Button>
                {selectedMerchant.status === 'active' ? (
                  <Button
                    variant="destructive"
                    disabled={actionLoading}
                    onClick={() => {
                      handleSuspend(selectedMerchant.id);
                      setSelectedMerchant(null);
                    }}
                  >
                    {actionLoading ? t('loading') : t('suspend')}
                  </Button>
                ) : (
                  <Button
                    disabled={actionLoading}
                    onClick={() => {
                      handleActivate(selectedMerchant.id);
                      setSelectedMerchant(null);
                    }}
                  >
                    {actionLoading ? t('loading') : t('activate')}
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={activationModal.show}
        onOpenChange={(open) => {
          if (!open && activationModal.type !== 'loading') closeActivationModal();
        }}
      >
        <DialogContent className="max-w-md">
          {activationModal.type === 'loading' && (
            <div className="py-6 text-center">
              <div className="mb-4 flex justify-center">
                <Loader2 className="h-12 w-12 animate-spin text-primary" />
              </div>
              <DialogHeader>
                <DialogTitle>{activationModal.message}</DialogTitle>
                <DialogDescription>{t('loading')}</DialogDescription>
              </DialogHeader>
            </div>
          )}

          {activationModal.type === 'success' && (
            <div className="space-y-4">
              <div className="flex justify-center">
                <div className="rounded-full bg-emerald-500/15 p-3">
                  <CheckCircle2 className="h-12 w-12 text-emerald-600 dark:text-emerald-400" />
                </div>
              </div>
              <DialogHeader>
                <DialogTitle className="text-center">{t('activationSuccess')}</DialogTitle>
                <DialogDescription className="text-center">{activationModal.message}</DialogDescription>
              </DialogHeader>
              {activationModal.data && (
                <div className="rounded-lg bg-muted/50 p-4 text-sm text-muted-foreground">
                  <p>{t('userAccountCreated')}</p>
                  {activationModal.data.password_setup?.email_sent && (
                    <p className="mt-1">{t('passwordResetLinkSent')}</p>
                  )}
                </div>
              )}
              <Button className="w-full" onClick={closeActivationModal}>
                {t('close')}
              </Button>
            </div>
          )}

          {activationModal.type === 'partial' && (
            <div className="space-y-4">
              <div className="flex justify-center">
                <div className="rounded-full bg-amber-500/15 p-3">
                  <AlertTriangle className="h-12 w-12 text-amber-600 dark:text-amber-400" />
                </div>
              </div>
              <DialogHeader>
                <DialogTitle className="text-center">{t('activationWarning')}</DialogTitle>
                <DialogDescription className="text-center">
                  {t('merchantActivatedSuccessfully')}
                </DialogDescription>
              </DialogHeader>
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-900/20">
                <p className="mb-2 font-medium text-amber-800 dark:text-amber-300">
                  {t('emailFailedToSend')}
                </p>
                <div className="space-y-1 text-sm text-amber-700 dark:text-amber-400">
                  <p>{t('userAccountCreated')}</p>
                  <p>{t('emailFailedToSend')}</p>
                </div>
              </div>
              <p className="text-center text-sm text-muted-foreground">{t('pleaseResendEmail')}</p>
              <DialogFooter className="gap-2 sm:justify-stretch">
                <Button
                  className="flex-1"
                  onClick={() => handleResendEmail(activationModal.merchantId)}
                >
                  {t('resendEmail')}
                </Button>
                <Button className="flex-1" variant="outline" onClick={closeActivationModal}>
                  {t('close')}
                </Button>
              </DialogFooter>
            </div>
          )}

          {activationModal.type === 'error' && (
            <div className="space-y-4">
              <div className="flex justify-center">
                <div className="rounded-full bg-destructive/15 p-3">
                  <XCircle className="h-12 w-12 text-destructive" />
                </div>
              </div>
              <DialogHeader>
                <DialogTitle className="text-center">{t('activationError')}</DialogTitle>
                <DialogDescription className="text-center text-destructive">
                  {activationModal.message}
                </DialogDescription>
              </DialogHeader>
              {activationModal.data && (
                <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                  <pre className="whitespace-pre-wrap">
                    {JSON.stringify(activationModal.data, null, 2)}
                  </pre>
                </div>
              )}
              <DialogFooter className="gap-2 sm:justify-stretch">
                <Button
                  className="flex-1"
                  onClick={() => {
                    closeActivationModal();
                    handleActivate(activationModal.merchantId);
                  }}
                >
                  {t('retry')}
                </Button>
                <Button className="flex-1" variant="outline" onClick={closeActivationModal}>
                  {t('close')}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {actionError && <div className="mt-4 text-center text-destructive">{actionError}</div>}
    </div>
  );
};

export default Merchants;
